import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PanelFlotante from "../../components/PanelFlotante";

// 🎂 Cumpleaños — Supervisora → Servicios.
// Cumpleaños de los alumnos de los pisos de ESTA supervisora. La fecha de cada
// alumno sale de /api/cumpleanos/* (el backend decide la prioridad: primero lo
// añadido a mano, luego lo que trae el Sheet). Aquí solo se pintan los datos.

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
  "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const DIAS_SEMANA = ["L", "M", "X", "J", "V", "S", "D"];
const PALETA_CHIP = ["#fb7185", "#f59e0b", "#a78bfa", "#34d399", "#60a5fa", "#f472b6"];

const pad2 = (n) => String(n).padStart(2, "0");
const isoLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const hoy = () => new Date();
const lunesDe = (d) => {
  const n = new Date(d);
  n.setDate(n.getDate() - ((n.getDay() + 6) % 7));
  n.setHours(0, 0, 0, 0);
  return n;
};
const sumarDias = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const ultimoDiaMes = (anio, mes1a12) => new Date(anio, mes1a12, 0).getDate();
const anioDe = (fechaISO) => Number(fechaISO.slice(0, 4));
const EDAD_MINIMA = 17;

// Edad exacta (en años cumplidos) a partir de una fecha "YYYY-MM-DD". Mismo
// cálculo que hace el backend al guardar, para avisar aquí antes de enviar.
const calcularEdad = (fechaISO) => {
  if (!fechaISO) return null;
  const [y, m, d] = fechaISO.split("-").map(Number);
  const n = hoy();
  let edad = n.getFullYear() - y;
  if (n.getMonth() + 1 < m || (n.getMonth() + 1 === m && n.getDate() < d)) edad -= 1;
  return edad;
};

// Rejilla de semanas (lunes-domingo) de un mes concreto, con huecos (null) al
// principio/final para que las columnas de días de la semana cuadren siempre.
function rejillaMes(anio, mes1a12) {
  const primerHueco = (new Date(anio, mes1a12 - 1, 1).getDay() + 6) % 7;
  const totalDias = ultimoDiaMes(anio, mes1a12);
  const celdas = [...Array(primerHueco).fill(null), ...Array.from({ length: totalDias }, (_, i) => i + 1)];
  while (celdas.length % 7 !== 0) celdas.push(null);
  const semanas = [];
  for (let i = 0; i < celdas.length; i += 7) semanas.push(celdas.slice(i, i + 7));
  return semanas;
}

const UMBRAL_ESCRITORIO = 900;

// En pantallas de escritorio hay sitio de sobra: se agranda el calendario y
// se enseñan los nombres directamente en la celda, sin tener que pulsar el día.
function useEsEscritorio() {
  const [ancho, setAncho] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));
  useEffect(() => {
    const onResize = () => setAncho(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return ancho >= UMBRAL_ESCRITORIO;
}

function Confeti() {
  // Decoración SIEMPRE visible (haya o no cumpleaños hoy): globos/confeti
  // flotando sobre la cabecera, puramente de ambiente.
  const piezas = useMemo(() => (
    ["🎈", "🎉", "✨", "🎁", "🎈", "🎊", "✨", "🎈"].map((emoji, i) => ({
      emoji, left: 4 + i * 12 + (i % 2) * 3, delay: -(i * 1.3), dur: 6 + (i % 3),
      size: 20 + (i % 3) * 6,
    }))
  ), []);
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {piezas.map((p, i) => (
        <span key={i} style={{
          position: "absolute", left: `${p.left}%`, top: "60%", fontSize: p.size,
          animation: `cumple_flotar ${p.dur}s ease-in-out ${p.delay}s infinite`,
          opacity: 0.85, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.12))",
        }}>{p.emoji}</span>
      ))}
    </div>
  );
}

const Cumpleanos = () => {
  const navigate = useNavigate();
  const esEscritorio = useEsEscritorio();
  const anchoMax = esEscritorio ? 1180 : 980;
  const [vista, setVista] = useState("mes"); // "mes" | "semana"
  const [mesActual, setMesActual] = useState(hoy().getMonth() + 1);
  const [anioRef] = useState(hoy().getFullYear()); // solo para construir la rejilla del mes
  const [semanaBase, setSemanaBase] = useState(() => lunesDe(hoy()));

  const [cumples, setCumples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [pendientes, setPendientes] = useState({ total: 0, alumnos: [] });
  const [modalAbierto, setModalAbierto] = useState(false);

  const [panelDia, setPanelDia] = useState(null); // { titulo, gente: [...] } | null
  const anchorDiaRef = useRef(null);
  const [menuPdfAbierto, setMenuPdfAbierto] = useState(false);
  const anchorPdfRef = useRef(null);
  const [descargando, setDescargando] = useState(false);

  const cargarPendientes = useCallback(() => {
    axios.get("/api/cumpleanos/pendientes")
      .then((r) => setPendientes(r.data || { total: 0, alumnos: [] }))
      .catch(() => {});
  }, []);

  useEffect(() => { cargarPendientes(); }, [cargarPendientes]);

  // Rango real (desde/hasta) que toca pedir según la vista actual.
  const rango = useMemo(() => {
    if (vista === "semana") {
      return { desde: isoLocal(semanaBase), hasta: isoLocal(sumarDias(semanaBase, 6)) };
    }
    const ultimo = ultimoDiaMes(anioRef, mesActual);
    return { desde: `${anioRef}-${pad2(mesActual)}-01`, hasta: `${anioRef}-${pad2(mesActual)}-${pad2(ultimo)}` };
  }, [vista, mesActual, anioRef, semanaBase]);

  useEffect(() => {
    let vivo = true;
    setLoading(true); setError("");
    axios.get("/api/cumpleanos/rango", { params: rango })
      .then((r) => { if (vivo) setCumples(r.data?.cumpleanos || []); })
      .catch(() => { if (vivo) setError("❌ No se pudieron cargar los cumpleaños."); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [rango]);

  const porDia = useMemo(() => {
    const m = new Map();
    cumples.forEach((c) => { if (!m.has(c.dia)) m.set(c.dia, []); m.get(c.dia).push(c); });
    return m;
  }, [cumples]);

  const esHoyReal = (anio, mes1a12, dia) => {
    const h = hoy();
    return anio === h.getFullYear() && mes1a12 === h.getMonth() + 1 && dia === h.getDate();
  };

  const abrirDia = (e, dia, gente) => {
    if (!gente || gente.length === 0) return;
    anchorDiaRef.current = e.currentTarget;
    // Años que cumple: el año de referencia del calendario (anioRef) menos el
    // año de nacimiento real guardado (no solo día/mes).
    const conEdad = gente.map((g) => ({ ...g, cumpleEdad: anioRef - anioDe(g.fecha_nacimiento) }));
    setPanelDia({ titulo: `🎂 ${dia} de ${MESES[mesActual - 1]}`, gente: conEdad });
  };

  const cambiarMes = (delta) => setMesActual((m) => ((m - 1 + delta + 12) % 12) + 1);

  const semanaEtiqueta = `${isoLocal(semanaBase).slice(8, 10)} ${MESES[semanaBase.getMonth()].slice(0, 3)} – ` +
    `${isoLocal(sumarDias(semanaBase, 6)).slice(8, 10)} ${MESES[sumarDias(semanaBase, 6).getMonth()].slice(0, 3)}`;

  const descargarPdf = async (params, nombreSugerido) => {
    setMenuPdfAbierto(false);
    setDescargando(true);
    try {
      const res = await axios.get("/api/cumpleanos/pdf", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = nombreSugerido;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("No se ha podido generar el PDF.");
    } finally {
      setDescargando(false);
    }
  };

  const guardarFecha = async (idAlumno, fechaISO) => {
    if (!fechaISO) return;
    try {
      await axios.post(`/api/cumpleanos/${idAlumno}`, { fecha_nacimiento: fechaISO });
      setPendientes((prev) => ({ total: prev.total - 1, alumnos: prev.alumnos.filter((a) => a.id_alumno !== idAlumno) }));
      // refresca la vista actual por si el nuevo cumpleaños cae en el rango visible
      axios.get("/api/cumpleanos/rango", { params: rango }).then((r) => setCumples(r.data?.cumpleanos || [])).catch(() => {});
    } catch (e) {
      alert(e.response?.data?.error || "No se ha podido guardar la fecha.");
    }
  };

  return (
    <div style={styles.pagina}>
      <style>{CSS}</style>

      <div style={{ ...styles.barraSuperior, maxWidth: anchoMax }}>
        <button onClick={() => navigate("/supervisora/servicios")} style={styles.btnVolver}>← Volver</button>
      </div>

      <div style={{ ...styles.hero, maxWidth: anchoMax }}>
        <Confeti />
        <div style={styles.heroContenido}>
          <div style={styles.heroCake}>🎂</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={styles.heroTitulo}>Cumpleaños</h1>
            <p style={styles.heroSub}>de los alumnos de tus pisos</p>
            <button
              onClick={() => setModalAbierto(true)}
              className={pendientes.total > 0 ? "cumple-pulso cumple-pendientes-activo" : "cumple-pendientes-vacio"}
              style={pendientes.total > 0 ? styles.btnPendientesActivo : styles.btnPendientesVacio}
            >
              {pendientes.total > 0 ? `🎉 Cumpleaños pendientes (${pendientes.total})` : "✅ Sin cumpleaños pendientes"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ ...styles.controles, maxWidth: anchoMax }}>
        <div style={styles.toggleVista}>
          <div style={{
            ...styles.toggleIndicador,
            transform: `translateX(${vista === "semana" ? "100%" : "0%"})`,
            background: vista === "semana" ? "linear-gradient(135deg, #22d3ee, #0891b2)" : "linear-gradient(135deg, #f472b6, #a855f7)",
          }} />
          {["mes", "semana"].map((v) => (
            <button key={v} onClick={() => setVista(v)} className="cumple-toggle-btn"
              style={{ ...styles.toggleBtn, ...(vista === v ? styles.toggleBtnActivoTexto : {}) }}>
              {v === "mes" ? "📅 Mes" : "🗓️ Semana"}
            </button>
          ))}
        </div>

        {vista === "mes" ? (
          <div style={styles.navegador}>
            <button onClick={() => cambiarMes(-1)} className="cumple-nav" style={styles.navBtn} aria-label="Mes anterior">‹</button>
            <select value={mesActual} onChange={(e) => setMesActual(Number(e.target.value))} style={styles.selectMes}>
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
            </select>
            <button onClick={() => cambiarMes(1)} className="cumple-nav" style={styles.navBtn} aria-label="Mes siguiente">›</button>
          </div>
        ) : (
          <div style={styles.navegador}>
            <button onClick={() => setSemanaBase((s) => sumarDias(s, -7))} className="cumple-nav" style={styles.navBtn} aria-label="Semana anterior">‹</button>
            <span style={styles.semanaLabel}>Semana del {semanaEtiqueta}</span>
            <button onClick={() => setSemanaBase((s) => sumarDias(s, 7))} className="cumple-nav" style={styles.navBtn} aria-label="Semana siguiente">›</button>
            <button onClick={() => setSemanaBase(lunesDe(hoy()))} style={styles.btnHoy}>Hoy</button>
          </div>
        )}

        <button ref={anchorPdfRef} onClick={() => setMenuPdfAbierto((v) => !v)} disabled={descargando} style={styles.btnPdf}>
          {descargando ? "⏳ Generando…" : "📄 Descargar PDF"}
        </button>
      </div>

      {menuPdfAbierto && (
        <PanelFlotante anchorRef={anchorPdfRef} onCerrar={() => setMenuPdfAbierto(false)} style={styles.menuPdf}>
          <div style={styles.menuPdfCabecera}>
            <div style={styles.menuPdfIconoCabecera}>📄</div>
            <div>
              <div style={styles.menuPdfTitulo}>Descargar en PDF</div>
              <div style={styles.menuPdfSubtitulo}>Calendario, piso y años que cumple cada uno</div>
            </div>
          </div>
          <button className="cumple-menu-opcion" style={styles.menuPdfOpcion}
            onClick={() => descargarPdf({ modo: "mes", mes: mesActual }, `cumpleanos_${MESES[mesActual - 1]}.pdf`)}>
            <span style={{ ...styles.menuPdfIcono, background: "linear-gradient(135deg, #f472b6, #a855f7)" }}>📅</span>
            <span style={styles.menuPdfTexto}>
              <span style={styles.menuPdfOpcionTitulo}>Este mes</span>
              <span style={styles.menuPdfOpcionSub}>{MESES[mesActual - 1].charAt(0).toUpperCase() + MESES[mesActual - 1].slice(1)}</span>
            </span>
            <span style={styles.menuPdfFlecha}>›</span>
          </button>
          <button className="cumple-menu-opcion" style={styles.menuPdfOpcion}
            onClick={() => descargarPdf({ modo: "semana", desde: isoLocal(semanaBase), hasta: isoLocal(sumarDias(semanaBase, 6)) }, `cumpleanos_semana_${isoLocal(semanaBase)}.pdf`)}>
            <span style={{ ...styles.menuPdfIcono, background: "linear-gradient(135deg, #22d3ee, #0891b2)" }}>🗓️</span>
            <span style={styles.menuPdfTexto}>
              <span style={styles.menuPdfOpcionTitulo}>Esta semana</span>
              <span style={styles.menuPdfOpcionSub}>Del {semanaEtiqueta}</span>
            </span>
            <span style={styles.menuPdfFlecha}>›</span>
          </button>
          <button className="cumple-menu-opcion" style={styles.menuPdfOpcion}
            onClick={() => descargarPdf({ modo: "todos" }, "cumpleanos_todo_el_anio.pdf")}>
            <span style={{ ...styles.menuPdfIcono, background: "linear-gradient(135deg, #fde047, #f59e0b)" }}>📖</span>
            <span style={styles.menuPdfTexto}>
              <span style={styles.menuPdfOpcionTitulo}>Todo el año</span>
              <span style={styles.menuPdfOpcionSub}>Los 12 meses, uno por página</span>
            </span>
            <span style={styles.menuPdfFlecha}>›</span>
          </button>
        </PanelFlotante>
      )}

      {error && <p style={{ textAlign: "center", color: "#dc2626" }}>{error}</p>}

      {vista === "mes" ? (
        <div key={`mes-${mesActual}`} className="cumple-fade" style={{ ...styles.tarjetaCalendario, maxWidth: anchoMax }}>
          <div style={styles.filaSemana}>
            {DIAS_SEMANA.map((d) => <div key={d} style={styles.cabeceraDia}>{d}</div>)}
          </div>
          {rejillaMes(anioRef, mesActual).map((semana, si) => (
            <div key={si} style={styles.filaSemana}>
              {semana.map((dia, di) => {
                if (dia === null) return <div key={di} style={styles.celdaVacia} />;
                const gente = porDia.get(dia) || [];
                const tieneCumple = gente.length > 0;
                const esHoy = esHoyReal(anioRef, mesActual, dia);
                return (
                  <button key={di} onClick={(e) => abrirDia(e, dia, gente)}
                    className={tieneCumple ? "cumple-dia-cumple" : undefined}
                    style={{
                      ...styles.celda,
                      ...(esEscritorio ? styles.celdaEscritorio : {}),
                      ...(tieneCumple ? styles.celdaConCumple : {}),
                      ...(esHoy ? styles.celdaHoy : {}),
                      cursor: tieneCumple ? "pointer" : "default",
                      alignItems: esEscritorio ? "stretch" : "center",
                    }}>
                    <span style={{ ...styles.numeroDia, ...(esEscritorio ? styles.numeroDiaEscritorio : {}) }}>{dia}</span>
                    {tieneCumple && (esEscritorio ? (
                      // Escritorio: toda la info ya a la vista, sin tener que pulsar.
                      <div style={styles.listaEscritorio}>
                        {gente.map((g) => (
                          <div key={g.id_alumno} style={styles.nombreEscritorio}>
                            🎂 {g.nombre.split(" ")[0]} <span style={styles.pisoEscritorio}>· {g.abreviatura_piso}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <span style={styles.insigniaCumple}>
                          🎂{gente.length > 1 ? ` ${gente.length}` : ""}
                        </span>
                        {gente.length === 1 && (
                          <span style={styles.nombreEnCelda}>{gente[0].nombre.split(" ")[0]}</span>
                        )}
                      </>
                    ))}
                  </button>
                );
              })}
            </div>
          ))}
          {!loading && cumples.length === 0 && (
            <p style={styles.vacioMes}>🎈 Todavía no hay cumpleaños registrados en {MESES[mesActual - 1]}.</p>
          )}
        </div>
      ) : (
        <div key={`sem-${isoLocal(semanaBase)}`} className="cumple-fade" style={{ ...styles.filaSemanaVista, maxWidth: anchoMax }}>
          {Array.from({ length: 7 }, (_, i) => sumarDias(semanaBase, i)).map((fecha, i) => {
            const gente = porDia.get(fecha.getDate())?.filter((c) => c.mes === fecha.getMonth() + 1) || [];
            const esHoy = isoLocal(fecha) === isoLocal(hoy());
            return (
              <div key={i} style={{ ...styles.filaDiaSemana, ...(esHoy ? styles.filaDiaSemanaHoy : {}) }}>
                <div className="cumple-dia-etiqueta" style={styles.filaDiaEtiqueta}>
                  <span style={styles.filaDiaNombreSemana}>{["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"][i]}</span>
                  <span style={styles.filaDiaNumero}>{fecha.getDate()}</span>
                  <span style={styles.filaDiaMes}>{MESES[fecha.getMonth()].slice(0, 3)}</span>
                </div>
                <div style={styles.filaDiaCuerpo}>
                  {gente.length === 0 ? (
                    <span style={styles.sinCumpleSemana}>🎈 Sin cumpleaños</span>
                  ) : gente.map((c, gi) => (
                    <div key={c.id_alumno} style={{ ...styles.chipPersona, background: PALETA_CHIP[gi % PALETA_CHIP.length] + "22", borderColor: PALETA_CHIP[gi % PALETA_CHIP.length] + "55" }}>
                      <div style={styles.chipNombre}>🎂 {c.nombre}</div>
                      <div style={styles.chipPiso}>{c.abreviatura_piso} · Cumple {fecha.getFullYear() - anioDe(c.fecha_nacimiento)} años</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {panelDia && (
        <PanelFlotante anchorRef={anchorDiaRef} onCerrar={() => setPanelDia(null)} style={styles.panelDia}>
          <div style={styles.panelDiaTitulo}>{panelDia.titulo}</div>
          {panelDia.gente.map((g) => (
            <div key={g.id_alumno} style={styles.panelDiaFila}>
              <span style={styles.panelDiaNombre}>{g.nombre}</span>
              <span style={styles.panelDiaPiso}>{g.abreviatura_piso}{g.habitacion ? ` · ${g.habitacion}` : ""}</span>
              <span style={styles.panelDiaEdad}>🎂 Cumple {g.cumpleEdad} años</span>
            </div>
          ))}
        </PanelFlotante>
      )}

      {modalAbierto && (
        <ModalPendientes
          pendientes={pendientes}
          onGuardar={guardarFecha}
          onCerrar={() => setModalAbierto(false)}
        />
      )}
    </div>
  );
};

function ModalPendientes({ pendientes, onGuardar, onCerrar }) {
  const [fechas, setFechas] = useState({}); // id_alumno -> "YYYY-MM-DD"
  const [errores, setErrores] = useState({}); // id_alumno -> texto de error | null
  const [guardando, setGuardando] = useState(null);

  const cambiarFecha = (id, valor) => {
    setFechas((prev) => ({ ...prev, [id]: valor }));
    const edad = calcularEdad(valor);
    setErrores((prev) => ({
      ...prev,
      [id]: valor && edad != null && edad < EDAD_MINIMA
        ? `Con esa fecha tendría ${edad} años: revisa que el año de nacimiento esté bien escrito.`
        : null,
    }));
  };

  const confirmar = async (id) => {
    if (!fechas[id] || errores[id]) return;
    setGuardando(id);
    await onGuardar(id, fechas[id]);
    setGuardando(null);
  };

  return (
    <div style={styles.overlay} onClick={onCerrar}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalCabecera}>
          <h2 style={styles.modalTitulo}>🎉 Cumpleaños pendientes ({pendientes.total})</h2>
          <button onClick={onCerrar} style={styles.modalCerrar} aria-label="Cerrar">✕</button>
        </div>
        <div style={styles.modalCuerpo}>
          {pendientes.total === 0 ? (
            <div style={styles.modalVacio}>
              <div style={{ fontSize: 48 }}>🎉</div>
              <p>¡Todo al día! Todas las fechas de cumpleaños están registradas.</p>
            </div>
          ) : pendientes.alumnos.map((a) => {
            const error = errores[a.id_alumno];
            return (
              <div key={a.id_alumno} style={styles.filaPendiente}>
                <div style={styles.filaPendienteInfo}>
                  <div style={styles.filaPendienteNombre}>{a.nombre}</div>
                  <div style={styles.filaPendientePiso}>{a.abreviatura_piso}{a.habitacion ? ` · ${a.habitacion}` : ""}</div>
                </div>
                <div style={styles.filaPendienteAccionCol}>
                  <div style={styles.filaPendienteAccion}>
                    <label htmlFor={`fecha-${a.id_alumno}`} style={styles.labelFecha}>Fecha de nacimiento</label>
                    <input id={`fecha-${a.id_alumno}`} type="date" value={fechas[a.id_alumno] || ""}
                      max={isoLocal(hoy())}
                      onChange={(e) => cambiarFecha(a.id_alumno, e.target.value)}
                      style={{ ...styles.inputFecha, ...(error ? styles.inputFechaError : {}) }} />
                    <button onClick={() => confirmar(a.id_alumno)} disabled={!fechas[a.id_alumno] || !!error || guardando === a.id_alumno}
                      style={{ ...styles.btnGuardarFecha, opacity: !fechas[a.id_alumno] || !!error || guardando === a.id_alumno ? 0.5 : 1 }}>
                      {guardando === a.id_alumno ? "Guardando…" : "+ Añadir"}
                    </button>
                  </div>
                  {error && <div style={styles.errorFecha}>⚠️ {error}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const CSS = `
@keyframes cumple_flotar {
  0%, 100% { transform: translateY(0) rotate(-4deg); }
  50% { transform: translateY(-14px) rotate(4deg); }
}
@keyframes cumple_pulso {
  0%, 100% { box-shadow: 0 3px 10px rgba(245,158,11,0.45); }
  50% { box-shadow: 0 3px 18px rgba(245,158,11,0.8); }
}
@keyframes cumple_fade {
  from { opacity: 0.25; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
.cumple-pulso { animation: cumple_pulso 1.8s ease-in-out infinite; }
.cumple-fade { animation: cumple_fade 0.28s ease; }
.cumple-dia-cumple:hover { transform: translateY(-3px) scale(1.03); box-shadow: 0 8px 18px rgba(168,85,247,0.35) !important; }
.cumple-dia-cumple:active { transform: translateY(-1px) scale(0.99); }
.cumple-nav:hover { background: #ede9fe; }
.cumple-pendientes-activo:hover { filter: brightness(1.06); transform: translateY(-1px); }
.cumple-pendientes-vacio:hover { background: rgba(255,255,255,0.26) !important; }
.cumple-toggle-btn:hover { color: #1e293b; }
.cumple-menu-opcion:hover { background: #f5f3ff; transform: translateX(2px); }
.cumple-menu-opcion { transition: background 0.15s ease, transform 0.15s ease; }
.cumple-nav, .cumple-dia-cumple { transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease; }
.cumple-pendientes-vacio, .cumple-pendientes-activo, .cumple-toggle-btn { transition: transform 0.15s ease, color 0.2s ease, filter 0.15s ease; }
@media (max-width: 640px) {
  .cumple-dia-etiqueta { min-width: 64px !important; }
}
`;

const styles = {
  pagina: {
    minHeight: "100vh", padding: "20px 16px 60px",
    background: "linear-gradient(180deg, #fff1f8 0%, #fef6e4 55%, #f5f3ff 100%)",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  barraSuperior: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, maxWidth: 980, margin: "0 auto 14px" },
  btnVolver: {
    padding: "10px 20px", borderRadius: 20, background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
    color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 14,
    boxShadow: "0 2px 8px rgba(29,78,216,0.3)",
  },
  hero: {
    position: "relative", maxWidth: 980, margin: "0 auto 18px", borderRadius: 24, overflow: "hidden",
    background: "linear-gradient(120deg, #f472b6, #a855f7 55%, #6366f1)", padding: "28px 24px", minHeight: 110,
    boxShadow: "0 8px 24px rgba(99,102,241,0.25)",
  },
  heroContenido: { position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 18 },
  heroCake: {
    fontSize: 54, lineHeight: 1, background: "rgba(255,255,255,0.22)", borderRadius: "50%",
    width: 86, height: 86, display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.4)", flexShrink: 0,
  },
  heroTitulo: { margin: 0, color: "#fff", fontSize: 32, fontWeight: 900, letterSpacing: 0.3, textShadow: "0 2px 6px rgba(0,0,0,0.18)" },
  heroSub: { margin: "2px 0 12px", color: "rgba(255,255,255,0.92)", fontSize: 15, fontWeight: 600 },
  // Botón "pendientes": color propio (ámbar/oro) para distinguirlo del rosa-morado
  // de la cabecera; si no hay ninguno pendiente, se apaga (cristal translúcido,
  // sin brillo) para no reclamar atención sobre algo que ya está al día.
  btnPendientesActivo: {
    display: "inline-block", padding: "10px 18px", borderRadius: 999,
    background: "linear-gradient(135deg, #fde047, #f59e0b)", color: "#78350f",
    border: "none", cursor: "pointer", fontWeight: 900, fontSize: 14.5,
    boxShadow: "0 3px 10px rgba(245,158,11,0.45)",
  },
  btnPendientesVacio: {
    display: "inline-block", padding: "7px 16px", borderRadius: 999,
    background: "rgba(255,255,255,0.16)", color: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", fontWeight: 700, fontSize: 12.5,
  },

  controles: {
    maxWidth: 980, margin: "0 auto 16px", display: "flex", justifyContent: "space-between",
    alignItems: "center", flexWrap: "wrap", gap: 12,
  },
  // Interruptor Mes/Semana: fondo deslizante animado (translateX), con un color
  // propio para cada modo (rosa-morado = mes, turquesa = semana), para que se
  // note de un vistazo cuál está activo y qué tipo de vista es.
  toggleVista: { position: "relative", display: "flex", background: "#fff", borderRadius: 999, padding: 4, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", width: 192 },
  toggleIndicador: { position: "absolute", top: 4, left: 4, bottom: 4, width: "calc(50% - 4px)", borderRadius: 999, transition: "transform 0.28s cubic-bezier(.4,0,.2,1), background 0.28s", boxShadow: "0 2px 8px rgba(0,0,0,0.18)" },
  toggleBtn: { position: "relative", zIndex: 1, flex: 1, border: "none", background: "transparent", padding: "9px 10px", borderRadius: 999, fontWeight: 800, fontSize: 13.5, color: "#64748b", cursor: "pointer", transition: "color 0.2s" },
  toggleBtnActivoTexto: { color: "#fff" },
  navegador: { display: "flex", alignItems: "center", gap: 8, background: "#fff", borderRadius: 16, padding: "6px 10px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)" },
  navBtn: { border: "none", background: "#f1f5f9", color: "#6d28d9", width: 32, height: 32, borderRadius: "50%", fontSize: 18, fontWeight: 800, cursor: "pointer" },
  selectMes: { border: "none", background: "transparent", fontWeight: 800, fontSize: 15, color: "#1e293b", cursor: "pointer", padding: "4px 6px" },
  semanaLabel: { fontWeight: 800, fontSize: 14, color: "#1e293b", padding: "0 4px", whiteSpace: "nowrap" },
  btnHoy: { border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#6d28d9", borderRadius: 999, padding: "6px 12px", fontWeight: 700, fontSize: 12.5, cursor: "pointer" },
  btnPdf: {
    border: "1px solid #e9d5ff", background: "#fff", color: "#7c3aed", borderRadius: 16, padding: "10px 16px",
    fontWeight: 800, fontSize: 13.5, cursor: "pointer", boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
  },
  // Desplegable de descarga: más grande y con tarjetas (icono + título +
  // detalle) en vez de simples líneas de texto, para que se note más y
  // quede claro qué va a llevar cada PDF antes de pulsar.
  menuPdf: { background: "#fff", borderRadius: 18, boxShadow: "0 16px 40px rgba(88,28,135,0.25)", padding: 10, width: 340, border: "1px solid #f3e8ff" },
  menuPdfCabecera: { display: "flex", alignItems: "center", gap: 12, padding: "8px 10px 12px", borderBottom: "1px solid #f1f5f9", marginBottom: 6 },
  menuPdfIconoCabecera: {
    width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #581c87, #a855f7)",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0,
  },
  menuPdfTitulo: { fontSize: 15.5, fontWeight: 900, color: "#1e293b" },
  menuPdfSubtitulo: { fontSize: 11.5, color: "#94a3b8", fontWeight: 600, marginTop: 1 },
  menuPdfOpcion: {
    display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", border: "none",
    background: "transparent", padding: "10px 8px", borderRadius: 12, cursor: "pointer", marginBottom: 2,
  },
  menuPdfIcono: {
    width: 38, height: 38, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 17, flexShrink: 0, boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  },
  menuPdfTexto: { display: "flex", flexDirection: "column", flex: 1, minWidth: 0 },
  menuPdfOpcionTitulo: { fontSize: 14.5, fontWeight: 800, color: "#1e293b" },
  menuPdfOpcionSub: { fontSize: 12, color: "#94a3b8", fontWeight: 600, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  menuPdfFlecha: { fontSize: 20, color: "#d8b4fe", fontWeight: 900, flexShrink: 0 },

  tarjetaCalendario: { maxWidth: 980, margin: "0 auto", background: "#fff", borderRadius: 20, padding: "16px 14px 20px", boxShadow: "0 4px 18px rgba(99,102,241,0.12)" },
  filaSemana: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 6 },
  cabeceraDia: { textAlign: "center", fontSize: 12, fontWeight: 800, color: "#a78bfa", padding: "4px 0" },
  celdaVacia: { minHeight: 64, borderRadius: 12 },
  celda: {
    minHeight: 64, borderRadius: 12, border: "1px solid #f1f5f9", background: "#fafafa",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    padding: 4, transition: "transform 0.12s ease, box-shadow 0.12s ease",
  },
  // En escritorio hay sitio de sobra: celda más alta, contenido pegado arriba
  // y a la izquierda, para que quepan todos los nombres sin recortar.
  celdaEscritorio: {
    minHeight: 112, justifyContent: "flex-start", alignItems: "stretch", padding: "8px 8px", gap: 3,
  },
  celdaConCumple: {
    background: "linear-gradient(145deg, #fce7f3, #ede9fe)", border: "1px solid #e9d5ff",
    boxShadow: "0 2px 8px rgba(168,85,247,0.18)",
  },
  celdaHoy: { boxShadow: "0 0 0 2px #f472b6 inset" },
  numeroDia: { fontSize: 13, fontWeight: 700, color: "#334155" },
  numeroDiaEscritorio: { fontSize: 15, textAlign: "left" },
  insigniaCumple: { fontSize: 11.5, fontWeight: 800, color: "#9333ea", marginTop: 2 },
  nombreEnCelda: { fontSize: 9.5, fontWeight: 700, color: "#6d28d9", marginTop: 1, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  listaEscritorio: { display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" },
  nombreEscritorio: {
    fontSize: 11, fontWeight: 800, color: "#6d28d9", textAlign: "left",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  pisoEscritorio: { fontSize: 9.5, fontWeight: 600, color: "#9333ea", opacity: 0.75 },
  vacioMes: { textAlign: "center", color: "#94a3b8", fontSize: 13.5, marginTop: 14, fontWeight: 600 },

  // Vista semana: un día por fila (etiqueta a la izquierda + cumpleaños de ese
  // día a la derecha, en una lista vertical fácil de leer de un vistazo).
  filaSemanaVista: { maxWidth: 980, margin: "0 auto", display: "flex", flexDirection: "column", gap: 10 },
  filaDiaSemana: {
    background: "#fff", borderRadius: 16, boxShadow: "0 2px 10px rgba(99,102,241,0.1)",
    overflow: "hidden", display: "flex", alignItems: "stretch", minHeight: 64,
  },
  filaDiaSemanaHoy: { boxShadow: "0 0 0 2px #f472b6, 0 2px 10px rgba(99,102,241,0.1)" },
  filaDiaEtiqueta: {
    background: "linear-gradient(135deg, #f472b6, #a855f7)", color: "#fff", minWidth: 84, flexShrink: 0,
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "8px 6px",
  },
  filaDiaNombreSemana: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4 },
  filaDiaNumero: { fontSize: 22, fontWeight: 900, lineHeight: 1.1 },
  filaDiaMes: { fontSize: 10, fontWeight: 700, opacity: 0.85, textTransform: "uppercase" },
  filaDiaCuerpo: { flex: 1, padding: "10px 14px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  sinCumpleSemana: { fontSize: 12.5, color: "#cbd5e1", fontWeight: 700 },
  chipPersona: { border: "1px solid", borderRadius: 10, padding: "6px 10px" },
  chipNombre: { fontSize: 12, fontWeight: 800, color: "#334155", lineHeight: 1.25 },
  chipPiso: { fontSize: 10.5, color: "#64748b", fontWeight: 600, marginTop: 1 },

  panelDia: { background: "#fff", borderRadius: 14, boxShadow: "0 8px 28px rgba(0,0,0,0.18)", padding: 14, minWidth: 220, maxWidth: 280 },
  panelDiaTitulo: { fontWeight: 800, fontSize: 14, color: "#6d28d9", marginBottom: 8 },
  panelDiaFila: { padding: "6px 0", borderTop: "1px solid #f1f5f9", display: "flex", flexDirection: "column" },
  panelDiaNombre: { fontSize: 13, fontWeight: 700, color: "#1e293b" },
  panelDiaPiso: { fontSize: 11.5, color: "#94a3b8" },
  panelDiaEdad: { fontSize: 11.5, color: "#be185d", fontWeight: 800, marginTop: 2 },

  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  modal: { background: "#fff", borderRadius: 20, width: "min(720px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" },
  modalCabecera: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px", background: "linear-gradient(135deg, #f472b6, #a855f7)" },
  modalTitulo: { margin: 0, color: "#fff", fontSize: 18, fontWeight: 800 },
  modalCerrar: { border: "none", background: "rgba(255,255,255,0.25)", color: "#fff", width: 32, height: 32, borderRadius: "50%", fontSize: 15, fontWeight: 800, cursor: "pointer" },
  modalCuerpo: { padding: "10px 16px 20px", overflowY: "auto" },
  modalVacio: { textAlign: "center", padding: "40px 20px", color: "#64748b", fontWeight: 600 },
  filaPendiente: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "12px 6px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" },
  filaPendienteInfo: { minWidth: 160 },
  filaPendienteNombre: { fontWeight: 700, fontSize: 14, color: "#1e293b" },
  filaPendientePiso: { fontSize: 12, color: "#94a3b8", marginTop: 2 },
  filaPendienteAccionCol: { display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" },
  filaPendienteAccion: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  labelFecha: { fontSize: 11.5, fontWeight: 700, color: "#94a3b8" },
  inputFecha: { border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 8px", fontSize: 13 },
  inputFechaError: { border: "1px solid #fca5a5", background: "#fff1f2" },
  errorFecha: { fontSize: 11.5, color: "#dc2626", fontWeight: 700, maxWidth: 260, textAlign: "right" },
  btnGuardarFecha: { border: "none", background: "linear-gradient(135deg, #f472b6, #a855f7)", color: "#fff", borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap" },
};

export default Cumpleanos;
